// src/Components/UserSideComponents/Home/TermsDeclarationModal.jsx
import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import toast from "react-hot-toast";
import axios from "../../Utils/axiosInstance";
import { useAuth } from "../Context/AuthContext";
import { generateTermsPDF } from "../../Utils/Pdf/generateTermsPDF";
import { assets } from "../../Assets/assets";
import { s3Upload } from "../../Utils/s3Upload";
import { sendTermsAcceptedEmail } from "../../Utils/emailjs";

// ── Summary declarations (stored in declaration_contents as before) ──
const DECLARATION_CONTENTS = [
  {
    content:
      "I understand that the terms and conditions outlined above govern my use of the Quick Quotation & Trade (QQT) platform and related services provided by PCM Exports.",
  }
];

// ── Full T&C sections (stored in sections[] for audit trail) ──
const TERMS_SECTIONS = [
  {
    heading: "1. Introduction",
    intro: 'Welcome to PCM Exports ("Company", "we", "our", "us"), an agro-commodity trading business established in 2010.',
    groups: [
      {
        items: [
          "We operate an AI-enabled quotation platform under the name Quick Quotation & Trade (QQT) that facilitates real-time pricing insights and trade coordination for agro-commodities including dry fruits, spices, pulses, grains, and millets.",
          "By accessing or using our website, services, or QQT platform, you agree to be legally bound by these Terms.",
        ],
      },
    ],
  },
  {
    heading: "2. Eligibility",
    groups: [
      {
        items: [
          "You must be at least 18 years old.",
          "You must be legally capable of entering into binding contracts.",
          "If you are representing a business entity, you confirm authority to bind that entity.",
        ],
      },
    ],
  },
  {
    heading: "3. Scope of Services",
    groups: [
      {
        subHeading: "PCM Exports provides:",
        items: [
          "AI-based indicative quotation generation.",
          "Trade facilitation between buyers and sellers.",
          "Support in sampling, quality coordination, logistics, and payments (where applicable).",
        ],
        note: "Important: We act as a facilitator and service provider, not a marketplace owner or inventory holder unless explicitly stated.",
      },
    ],
  },
  {
    heading: "4. Nature of Quotations",
    groups: [
      {
        items: [
          "Quotations generated by QQT are algorithm-based and indicative only.",
          "Prices are derived from market trends, user inputs, and available data.",
          "Quotations are non-binding unless confirmed via formal contract or invoice.",
        ],
      },
      {
        subHeading: "We do not guarantee:",
        items: [
          "Accuracy of real-time market prices.",
          "Availability of goods at quoted price.",
          "Final transaction execution.",
        ],
      },
    ],
  },
  {
    heading: "5. User Obligations",
    groups: [
      {
        subHeading: "You agree to:",
        items: [
          "Provide accurate, current, and complete information.",
          "Maintain confidentiality of account credentials.",
          "Use the platform only for lawful business purposes.",
          "Not engage in fraudulent, misleading, or manipulative activities.",
        ],
      },
      {
        subHeading: "You must not:",
        items: [
          "Use the platform to circulate false trade information.",
          "Attempt to reverse-engineer the AI system.",
          "Violate any applicable export/import laws.",
        ],
      },
    ],
  },
  {
    heading: "6. Trade and Transactions",
    groups: [
      {
        items: [
          "Transactions may occur directly between buyer and seller or via PCM Exports (if contracted).",
          "All key terms (price, quantity, quality, delivery, payment) must be mutually agreed.",
        ],
      },
      {
        subHeading: "PCM Exports:",
        items: [
          "Does not guarantee performance of either party.",
          "Is not responsible for breach of contract between buyer and seller.",
          "May assist in coordination but does not assume liability.",
        ],
      },
    ],
  },
  {
    heading: "7. Payments",
    groups: [
      {
        items: [
          "Payments may be processed through third-party gateways (e.g., Razorpay).",
          "Users agree to comply with payment provider terms.",
          "PCM Exports is not liable for payment delays due to banking systems or third-party processors.",
        ],
      },
    ],
  },
  {
    heading: "8. Logistics and Delivery",
    groups: [
      {
        items: [
          "Logistics services, if arranged, are handled by third-party providers.",
          "Delivery timelines are indicative and subject to external factors (weather, customs, transport issues).",
          "Risk transfer terms depend on agreed Incoterms (if applicable).",
        ],
      },
    ],
  },
  {
    heading: "9. Quality and Sampling",
    groups: [
      {
        items: [
          "Sampling and quality checks are based on mutually agreed standards.",
          "PCM Exports may facilitate quality checks but does not provide guarantees unless explicitly contracted.",
        ],
      },
    ],
  },
  {
    heading: "10. Intellectual Property",
    groups: [
      {
        subHeading: "All rights related to:",
        items: [
          "QQT platform.",
          "AI algorithms.",
          "Website content, branding, and design.",
        ],
        note: "These are owned exclusively by PCM Exports. Unauthorized use, copying, or distribution is strictly prohibited.",
      },
    ],
  },
  {
    heading: "11. Data Usage",
    groups: [
      {
        items: [
          "Use of the platform is governed by our Privacy Policy.",
          "By using the platform, you consent to data collection and usage as described therein.",
        ],
      },
    ],
  },
  {
    heading: "12. Limitation of Liability",
    groups: [
      {
        subHeading: "To the maximum extent permitted by law, PCM Exports shall not be liable for:",
        items: [
          "Loss of profits, business, or opportunity.",
          "Trade disputes between parties.",
          "Incorrect quotations due to system or data limitations.",
          "Delays, damages, or losses in logistics.",
          "Force majeure events.",
        ],
      },
    ],
  },
  {
    heading: "13. Indemnification",
    groups: [
      {
        subHeading: "You agree to indemnify and hold PCM Exports harmless against:",
        items: [
          "Claims arising from your misuse of the platform.",
          "Legal violations.",
          "Contractual disputes with third parties.",
        ],
      },
    ],
  },
  {
    heading: "14. Termination",
    groups: [
      {
        subHeading: "We reserve the right to:",
        items: [
          "Suspend or terminate access without prior notice.",
          "Remove users engaging in suspicious or unlawful activity.",
        ],
      },
    ],
  },
  {
    heading: "15. Governing Law and Jurisdiction",
    groups: [
      {
        items: [
          "These Terms shall be governed by the laws of India. Jurisdiction: Bhubaneswar, Odisha.",
        ],
      },
    ],
  },
  {
    heading: "16. Amendments",
    groups: [
      {
        items: [
          "We may update these Terms at any time. Continued use of the platform constitutes acceptance.",
        ],
      },
    ],
  },
];

// ── Convert an img src (URL or imported asset) to base64 data URL ────────────
const loadImageAsDataUrl = (src) =>
  new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      try {
        const canvas = document.createElement("canvas");
        canvas.width = img.naturalWidth;
        canvas.height = img.naturalHeight;
        canvas.getContext("2d").drawImage(img, 0, 0);
        resolve(canvas.toDataURL("image/png"));
      } catch {
        resolve(null);
      }
    };
    img.onerror = () => resolve(null);
    img.src = src;
  });

// ─────────────────────────────────────────────────────────────────────────────

const TermsDeclarationModal = ({ show, onAccepted }) => {
  const { user } = useAuth();
  const [isOpen, setIsOpen] = useState(show);
  const [animate, setAnimate] = useState(false);
  const [hasScrolled, setHasScrolled] = useState(false);
  const [checked, setChecked] = useState(false);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("terms"); // "terms" | "summary"
  const scrollRef = useRef(null);

  useEffect(() => {
    setIsOpen(show);
    if (show) {
      setHasScrolled(false);
      setChecked(false);
      setActiveTab("terms");
      setTimeout(() => setAnimate(true), 20);
    } else {
      setAnimate(false);
    }
  }, [show]);

  const handleScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 30;
    if (atBottom) setHasScrolled(true);
  };

  const handleConfirm = async () => {
    if (!checked || loading) return;
    setLoading(true);
    try {
      // 1️⃣ Save declaration to backend
      await axios.post("/declaration", {
        reason: "Terms & Conditions Acceptance",
        declaration_is_for: "Term_And_Condition",
        declaration_contents: DECLARATION_CONTENTS,
        sections: TERMS_SECTIONS,
        e_sign: user?.e_signature,
        date: new Date(),
        isMainDeclaration: true,
      });

      // 2️⃣ Load logo as base64 then generate + auto-download PDF
      const logoDataUrl = await loadImageAsDataUrl(assets.qqtlogo);
      const acceptedBy = user
        ? `${user.firstName || ""} ${user.lastName || ""}`.trim()
        : "";

      const pdf = generateTermsPDF({
        logoDataUrl,
        termsSections: TERMS_SECTIONS,
        declarationContents: DECLARATION_CONTENTS,
        acceptedBy,
      });

      // 3️⃣ Immediately close modal and notify parent
      setAnimate(false);
      setIsOpen(false);
      if (typeof onAccepted === "function") {
        onAccepted();
      }

      toast.success("Terms accepted! PDF downloaded.");

      // 4️⃣ Cloud upload & email notification in background (non-blocking)
      try {
        const pdfFile = new File(
          [pdf.blob],
          pdf.fileName,
          {
            type: "application/pdf",
          }
        );
        const uploadedPdf = await s3Upload(pdfFile, "declarations");
        if (uploadedPdf?.url) {
          await sendTermsAcceptedEmail({
            user,
            pdfUrl: uploadedPdf.url,
          });
        }
      } catch (cloudErr) {
        console.warn("[TermsDeclarationModal] Cloud upload or email notification failed:", cloudErr);
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to save acceptance");
    } finally {
      setLoading(false);
    }
  };

  if (!show || !isOpen) return null;

  return createPortal(
    <div
      className={`fixed inset-0 z-[9999] flex items-end md:items-center justify-center
        bg-black/40 backdrop-blur-sm transition-opacity duration-300
        ${animate ? "opacity-100" : "opacity-0"}`}
    >
      <div
        className={`
          bg-white w-full md:max-w-2xl md:rounded-3xl rounded-t-3xl shadow-2xl
          flex flex-col overflow-hidden transition-all duration-300 ease-out
          ${animate
            ? "translate-y-0 md:scale-100"
            : "translate-y-full md:translate-y-8 md:scale-95"
          }
        `}
        style={{ maxHeight: "92vh" }}
      >
        {/* ── Header ── */}
        <div className="bg-primaryColor px-5 py-5 relative overflow-hidden rounded-t-3xl flex-shrink-0">
          <div className="absolute -top-6 -right-6 w-28 h-28 rounded-full bg-white/10" />
          <div className="absolute top-2 -right-2 w-14 h-14 rounded-full bg-white/10" />
          <div className="absolute -bottom-4 -left-4 w-20 h-20 rounded-full bg-white/10" />
          <div className="relative z-10 text-center">
            {/* <div className="text-3xl mb-1.5">📋</div> */}
            <h2 className="text-white font-bold text-lg">Terms &amp; Conditions</h2>
            <p className="text-white/70 text-xs mt-1">
              Please read carefully before continuing
            </p>
          </div>
        </div>

        {/* ── Scrollable Content ── */}
        <div
          ref={scrollRef}
          onScroll={handleScroll}
          className="flex-1 overflow-y-auto px-5 py-5"
        >
          <p className="text-xs text-gray-400 leading-relaxed mb-4">
            Scroll to the bottom and check the box to confirm your agreement.
          </p>

          {activeTab === "terms" ? (
            <div className="space-y-6">
              {TERMS_SECTIONS.map((section, i) => (
                <div key={i}>
                  {/* Section heading */}
                  <h3 className="text-sm font-semibold text-gray-800 mb-2">
                    {section.heading}
                  </h3>

                  {/* Optional intro sentence */}
                  {section.intro && (
                    <p className="text-sm text-gray-600 leading-relaxed mb-2">
                      {section.intro}
                    </p>
                  )}

                  {/* Groups with optional sub-headings */}
                  <div className="space-y-3">
                    {(section.groups || []).map((group, gi) => (
                      <div key={gi}>
                        {/* Sub-heading e.g. "You agree to:" */}
                        {group.subHeading && (
                          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5 mt-2">
                            {group.subHeading}
                          </p>
                        )}

                        {/* Bullet items */}
                        {(group.items || []).length > 0 && (
                          <ul className="space-y-1.5 pl-1">
                            {group.items.map((item, j) => (
                              <li key={j} className="flex gap-2 text-sm text-gray-600 leading-relaxed">
                                <span className="text-primaryColor mt-1 flex-shrink-0">•</span>
                                <span>{item}</span>
                              </li>
                            ))}
                          </ul>
                        )}

                        {/* Note / callout e.g. "Important: ..." */}
                        {group.note && (
                          <div className="mt-2 flex gap-2 px-3 py-2 rounded-xl bg-amber-50 border border-amber-100">
                            <span className="text-amber-500 flex-shrink-0 mt-0.5">ℹ️</span>
                            <p className="text-xs text-amber-700 leading-relaxed">{group.note}</p>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="space-y-3">
              {DECLARATION_CONTENTS.map((item, i) => (
                <div
                  key={i}
                  className="flex gap-3 p-3.5 rounded-2xl bg-gray-50 border border-gray-100"
                >
                  <div className="w-6 h-6 rounded-full bg-primaryColor/10 text-primaryColor flex items-center justify-center flex-shrink-0 text-xs font-bold mt-0.5">
                    {i + 1}
                  </div>
                  <p className="text-sm text-gray-700 leading-relaxed">{item.content}</p>
                </div>
              ))}
            </div>
          )}

          {/* Scroll nudge */}
          {!hasScrolled && (
            <div className="flex items-center justify-center gap-1.5 text-xs text-gray-400 mt-5 animate-bounce">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
              Scroll down to continue
            </div>
          )}

          <div className="h-2" />
        </div>

        {/* ── Sticky Footer ── */}
        <div
          className={`flex-shrink-0 px-5 pb-7 pt-4 border-t border-gray-100 bg-white
            transition-all duration-500
            ${hasScrolled
              ? "opacity-100 translate-y-0 pointer-events-auto"
              : "opacity-30 translate-y-3 pointer-events-none"
            }`}
        >
          {/* PDF download notice */}
          {hasScrolled && (
            <div className="flex items-center gap-2 mb-3 px-3 py-2 bg-blue-50 border border-blue-100 rounded-xl">
              <span className="text-base">📥</span>
              <p className="text-xs text-blue-600">
                A PDF copy will be automatically downloaded when you accept.
              </p>
            </div>
          )}

          <label className="flex items-start gap-3 cursor-pointer mb-4 group select-none">
            <div className="relative mt-0.5 flex-shrink-0">
              <input
                type="checkbox"
                checked={checked}
                onChange={(e) => setChecked(e.target.checked)}
                disabled={!hasScrolled}
                className="sr-only"
              />
              <div
                className={`w-5 h-5 rounded-md border-2 flex items-center justify-center transition-all duration-200
                  ${checked
                    ? "bg-primaryColor border-primaryColor"
                    : "border-gray-300 bg-white group-hover:border-primaryColor/50"
                  }`}
              >
                {checked && (
                  <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                  </svg>
                )}
              </div>
            </div>
            <span className="text-sm text-gray-600 leading-snug">
              I understand that the terms and conditions outlined above govern my use of the Quick Quotation & Trade (QQT) platform and related services provided by PCM Exports.
            </span>
          </label>

          <button
            onClick={handleConfirm}
            disabled={!checked || loading}
            className={`w-full py-3.5 rounded-2xl font-semibold text-sm transition-all duration-200
              ${checked && !loading
                ? "bg-primaryColor text-white shadow-md shadow-primaryColor/25 active:scale-[0.98]"
                : "bg-gray-100 text-gray-400 cursor-not-allowed"
              }`}
          >
            {loading ? (
              <span className="flex items-center justify-center gap-2">
                <div className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                Saving & generating PDF...
              </span>
            ) : (
              "I Agree & Continue"
            )}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default TermsDeclarationModal;